import React, { useState, useEffect } from 'react';
import { authClient } from '../amplifyConfig';

function StatCard({ label, value, accent }) {
  return (
    <div className="stat-card" style={{ borderTopColor: accent || 'var(--gold-dim)' }}>
      <p className="stat-value" style={{ color: accent || 'var(--gold)' }}>{value}</p>
      <p className="stat-label">{label}</p>
    </div>
  );
}

function EngagementPanel({ title, rows, emptyMsg }) {
  return (
    <div className="engagement-panel">
      <p className="section-heading">{title}</p>
      {!rows.length ? (
        <p className="engagement-empty">{emptyMsg || 'No data yet'}</p>
      ) : (
        <div className="engagement-rows">
          {rows.map((row, i) => (
            <div key={i} className="engagement-row">
              <div className="engagement-bar-wrap">
                <span className="engagement-rank">#{i + 1}</span>
                <span className="engagement-title">{row.title || '—'}</span>
                <div className="engagement-bar">
                  <div
                    className="engagement-bar-fill"
                    style={{ width: `${Math.round((row.count / rows[0].count) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="engagement-count">{row.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OWNER_ID = '39e0b64c-068c-486e-b30d-fcd0525690cd';

function Analytics() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await authClient.models.AnalyticsEvent.list();
        setEvents((res.data || []).filter(e => e.visitorId !== OWNER_ID));
      } catch (e) {
        console.error('Failed to load analytics:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return (
    <p style={{ color: 'var(--text-dim)', letterSpacing: '3px', fontSize: '0.8rem' }}>LOADING...</p>
  );

  const totalVisits = events.filter(e => e.eventType === 'page_visit').length;
  const uniqueVisitors = new Set(events.filter(e => e.eventType === 'page_visit').map(e => e.visitorId)).size;
  const sectionCounts = { music: 0, poetry: 0, blog: 0 };
  events.filter(e => e.eventType === 'section_visit').forEach(e => {
    if (sectionCounts[e.contentTitle] !== undefined) sectionCounts[e.contentTitle]++;
  });

  const countByTitle = (type) => {
    const map = {};
    events.filter(e => e.eventType === type).forEach(e => {
      const key = e.contentTitle || 'Untitled';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([title, count]) => ({ title, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Most recent 5 visits
  const recentVisits = [...events]
    .filter(e => e.eventType === 'page_visit')
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 5);

  return (
    <div className="analytics">

      <div className="analytics-header">
        <p className="featured-label" style={{ padding: 0, marginBottom: '0.5rem' }}>Overview</p>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>
          Tracking since deployment · {events.length} total events recorded
        </p>
      </div>

      <div className="stat-grid">
        <StatCard label="Page Visits" value={totalVisits} accent="var(--gold)" />
        <StatCard label="Unique Visitors" value={uniqueVisitors} accent="#4a7eff" />
        <StatCard label="Music Views" value={sectionCounts.music} accent="#4a7eff" />
        <StatCard label="Poetry Views" value={sectionCounts.poetry} accent="var(--gold)" />
        <StatCard label="Blog Views" value={sectionCounts.blog} accent="var(--gold)" />
        <StatCard label="Total Events" value={events.length} accent="#4a7eff" />
      </div>

      <div className="analytics-panels">
        <EngagementPanel
          title="Video Plays"
          rows={countByTitle('video_play')}
          emptyMsg="No video plays recorded yet"
        />
        <EngagementPanel
          title="Poem Views"
          rows={countByTitle('poetry_click')}
          emptyMsg="No poem views recorded yet"
        />
        <EngagementPanel
          title="Blog Post Views"
          rows={countByTitle('blog_click')}
          emptyMsg="No blog views recorded yet"
        />
        <EngagementPanel
          title="Video Card Clicks"
          rows={countByTitle('video_card_click')}
          emptyMsg="No card clicks recorded yet"
        />
      </div>

      {recentVisits.length > 0 && (
        <div className="engagement-panel">
          <p className="section-heading">Recent Visits</p>
          <div className="engagement-rows">
            {recentVisits.map((e, i) => (
              <div key={i} className="engagement-row">
                <span className="engagement-title" style={{ color: 'var(--text-dim)' }}>
                  {new Date(e.timestamp).toLocaleString()}
                </span>
                <span className="engagement-count" style={{ color: 'var(--text-dim)', fontSize: '0.7rem' }}>
                  {e.visitorId.slice(0, 8)}...
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
